import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { Icons } from './Icons';
import { useLanguage } from '../contexts/LanguageContext';
import { APIService } from '../services/api';
import FeatureVoting from './FeatureVoting';

const FeedbackWidget = ({ user }) => {
    const { t } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [showFeatures, setShowFeatures] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [includeScreenshot, setIncludeScreenshot] = useState(true);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const [capturingScreenshot, setCapturingScreenshot] = useState(false);
    const [formData, setFormData] = useState({
        type: 'bug',
        severity: 'medium',
        description: '',
        email: user?.email || ''
    });
    
    const modalRef = useRef(null);
    
    const captureScreenshot = async () => {
        setCapturingScreenshot(true);
        try {
            if (modalRef.current) {
                modalRef.current.style.display = 'none';
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const canvas = await html2canvas(document.body, {
                useCORS: true,
                allowTaint: true,
                scale: 0.5,
                logging: false,
                foreignObjectRendering: false,
                removeContainer: true,
                imageTimeout: 5000,
                onclone: (clonedDoc) => {
                    const images = clonedDoc.querySelectorAll('img');
                    images.forEach(img => {
                        const src = img.src || '';
                        if (src.includes('gstatic.com') || 
                            src.includes('google.com/s2/favicons') || 
                            src.includes('faviconV2')) {
                            img.style.display = 'none';
                        }
                    });
                },
                ignoreElements: (element) => {
                    if (element.classList?.contains('feedback-widget') || 
                        element.classList?.contains('feedback-modal')) {
                        return true;
                    }
                    if (element.tagName === 'IMG') {
                        const src = element.src || '';
                        if (src.includes('gstatic.com') || 
                            src.includes('google.com/s2/favicons') ||
                            src.includes('faviconV2')) {
                            return true;
                        }
                    }
                    return false;
                }
            });
            
            const screenshot = canvas.toDataURL('image/jpeg', 0.7);
            setScreenshotPreview(screenshot);
            
            if (modalRef.current) {
                modalRef.current.style.display = 'flex';
            }
            
            return screenshot;
        } catch (error) {
            console.error('Screenshot capture failed:', error);
            return null;
        } finally {
            setCapturingScreenshot(false);
            if (modalRef.current) {
                modalRef.current.style.display = 'flex';
            }
        }
    };
    
    const handleOpen = () => {
        setIsOpen(true);
        setSubmitted(false);
        setScreenshotPreview(null);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.description.trim()) return;
        
        setIsSubmitting(true);
        try {
            let screenshot = screenshotPreview;
            if (includeScreenshot && !screenshot) {
                screenshot = await captureScreenshot();
            }
            
            const feedbackData = {
                ...formData,
                screenshot: includeScreenshot ? screenshot : null,
                url: window.location.href,
                userAgent: navigator.userAgent,
                screenSize: `${window.innerWidth}x${window.innerHeight}`,
                userId: user?.id || null,
                timestamp: new Date().toISOString()
            };
            
            await APIService.submitFeedback(feedbackData);
            setSubmitted(true);
            
            setTimeout(() => {
                setIsOpen(false);
                setFormData({
                    type: 'bug',
                    severity: 'medium',
                    description: '',
                    email: user?.email || ''
                });
                setScreenshotPreview(null);
            }, 2000);
        } catch (error) {
            console.error('Feedback submission failed:', error);
            alert(t('feedback.submitError') || 'Failed to submit feedback');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const typeOptions = [
        { value: 'bug', label: t('feedback.typeBug') || 'Bug / Chyba', icon: Icons.Bug },
        { value: 'feature', label: t('feedback.typeFeature') || 'Feature request', icon: Icons.Lightbulb },
        { value: 'improvement', label: t('feedback.typeImprovement') || 'Improvement', icon: Icons.TrendingUp },
        { value: 'other', label: t('feedback.typeOther') || 'Other', icon: Icons.MessageCircle }
    ];
    
    const severityOptions = [
        { value: 'low', label: t('feedback.severityLow') || 'Low', color: 'text-green-500' },
        { value: 'medium', label: t('feedback.severityMedium') || 'Medium', color: 'text-yellow-500' },
        { value: 'high', label: t('feedback.severityHigh') || 'High', color: 'text-orange-500' },
        { value: 'critical', label: t('feedback.severityCritical') || 'Critical', color: 'text-red-500' }
    ];
    
    return (
        <>
            <div className="feedback-widget fixed bottom-6 right-6 z-50 flex flex-col-reverse items-end gap-3">
                {expanded && (
                    <>
                        <button
                            onClick={() => { setShowFeatures(true); setExpanded(false); }}
                            className="w-12 h-12 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center animate-fade-in"
                            title={t('featureVoting.title') || 'Feature Requests'}
                        >
                            <Icons.Lightbulb className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => { handleOpen(); setExpanded(false); }}
                            className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center animate-fade-in"
                            title={t('feedback.title') || 'Report Bug'}
                        >
                            <Icons.Bug className="w-5 h-5" />
                        </button>
                    </>
                )}
                <button
                    onClick={() => setExpanded(!expanded)}
                    className={`w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center ${expanded ? 'rotate-45' : ''}`}
                    title={t('feedback.title') || 'Feedback'}
                >
                    {expanded ? (
                        <Icons.X className="w-6 h-6 transition-transform" />
                    ) : (
                        <Icons.MessageSquare className="w-6 h-6" />
                    )}
                </button>
            </div>
            
            {isOpen && (
                <div 
                    ref={modalRef}
                    className="feedback-modal cv-app-portal fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={(e) => e.target === modalRef.current && setIsOpen(false)}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <Icons.MessageSquare className="w-5 h-5 text-blue-600" />
                                {t('feedback.title') || 'Send Feedback'}
                            </h2>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <Icons.X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        
                        {submitted ? (
                            <div className="p-8 text-center">
                                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Icons.Check className="w-8 h-8 text-green-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('feedback.thankYou') || 'Thank you!'}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {t('feedback.submitted') || 'Your feedback has been submitted successfully.'}
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('feedback.typeLabel') || 'Feedback Type'}
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {typeOptions.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, type: option.value }))}
                                                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                                                    formData.type === option.value
                                                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                                }`}
                                            >
                                                <option.icon className="w-4 h-4" />
                                                <span className="text-sm font-medium">{option.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {formData.type === 'bug' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            {t('feedback.severityLabel') || 'Severity'}
                                        </label>
                                        <div className="flex gap-2">
                                            {severityOptions.map(option => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, severity: option.value }))}
                                                    className={`flex-1 p-2 rounded-lg border-2 text-sm font-medium transition-all ${
                                                        formData.severity === option.value
                                                            ? `border-current ${option.color} bg-current/10`
                                                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                                                    }`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('feedback.descriptionLabel') || 'Description'} *
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder={t('feedback.descriptionPlaceholder') || 'Describe the issue or your suggestion...'}
                                        className="w-full h-32 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('feedback.emailLabel') || 'Email (optional)'}
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder={t('feedback.emailPlaceholder') || 'your@email.com'}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Icons.Camera className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                            {t('feedback.includeScreenshot') || 'Include screenshot'}
                                        </span>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={includeScreenshot}
                                            onChange={(e) => setIncludeScreenshot(e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                                    </label>
                                </div>
                                
                                {includeScreenshot && (
                                    screenshotPreview ? (
                                        <div className="relative">
                                            <img
                                                src={screenshotPreview}
                                                alt="Screenshot preview"
                                                className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => captureScreenshot()}
                                                disabled={capturingScreenshot}
                                                className="absolute top-2 right-2 p-2 bg-white/90 dark:bg-gray-800/90 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors"
                                                title={t('feedback.retakeScreenshot') || 'Retake screenshot'}
                                            >
                                                <Icons.RefreshCw className={`w-4 h-4 text-gray-600 dark:text-gray-400 ${capturingScreenshot ? 'animate-spin' : ''}`} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => captureScreenshot()}
                                            disabled={capturingScreenshot}
                                            className="w-full p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
                                        >
                                            {capturingScreenshot ? (
                                                <>
                                                    <Icons.Loader className="w-4 h-4 animate-spin" />
                                                    {t('feedback.capturingScreenshot') || 'Capturing...'}
                                                </>
                                            ) : (
                                                <>
                                                    <Icons.Camera className="w-4 h-4" />
                                                    {t('feedback.captureScreenshot') || 'Capture screenshot'}
                                                </>
                                            )}
                                        </button>
                                    )
                                )}
                                
                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-2">
                                    <Icons.Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>
                                        {t('feedback.privacyNote') || 'Your current page URL and browser info will be included to help us understand the context.'}
                                    </span>
                                </div>
                                
                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                                    >
                                        {t('common.cancel') || 'Cancel'}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || !formData.description.trim()}
                                        className="flex-1 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Icons.Loader className="w-4 h-4 animate-spin" />
                                                {t('feedback.submitting') || 'Sending...'}
                                            </>
                                        ) : (
                                            <>
                                                <Icons.Send className="w-4 h-4" />
                                                {t('feedback.submit') || 'Send Feedback'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
            
            {showFeatures && (
                <div 
                    className="cv-app-portal fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={(e) => e.target === e.currentTarget && setShowFeatures(false)}
                >
                    <FeatureVoting user={user} onClose={() => setShowFeatures(false)} />
                </div>
            )}
        </>
    );
};

export default FeedbackWidget;
